import { translate } from '../../i18n/index.js';
import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AtomFitLogo, Spinner } from '../../components/design/AtomFitUI.js';
import { authApi } from '../../api/auth.api.js';

export function ResetPassword({ initial = false }: { initial?: boolean }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const redirectTo = getSafeRedirect(searchParams.get('redirect'));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password !== confirm) {
      setError(translate('pages.public.reset_password.handle_submit.set_error.password'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (initial) {
        await authApi.setInitialPassword(password);
        void navigate(redirectTo ?? '/admin', { replace: true });
      } else {
        await authApi.confirmPasswordReset(token, password);
        void navigate('/login?passwordReset=1', { replace: true });
      }
    } catch {
      setError(
        initial
          ? translate('pages.public.reset_password.handle_submit.set_error.error_password_create')
          : translate('pages.public.reset_password.handle_submit.set_error.password_2'),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      className="af-screen"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px 48px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: -200,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 700,
          height: 500,
          borderRadius: '50%',
          background: 'radial-gradient(ellipse, rgba(184,160,32,0.055) 0%, transparent 65%)',
        }}
      />

      <section style={{ width: 'min(420px, 100%)', position: 'relative' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <Link className="af-logo" to="/" style={{ fontSize: 20, justifyContent: 'center' }}>
            <AtomFitLogo />
          </Link>
        </div>

        <div
          className="af-card"
          style={{ padding: '32px 32px 28px', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}
        >
          {!token && !initial ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <p style={{ color: 'var(--af-muted)', fontSize: 14, marginBottom: 20 }}>
                {translate('pages.public.reset_password.reset_password.p.password')}
              </p>
              <Link to="/login" className="af-outline-btn">
                {translate('pages.public.reset_password.reset_password.link_outline_btn.back')}
              </Link>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 24 }}>
                <h1
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    margin: '0 0 6px',
                  }}
                >
                  {initial
                    ? translate('pages.public.reset_password.reset_password.h1.password_create')
                    : translate('pages.public.reset_password.reset_password.h1.password')}
                </h1>
                <p style={{ fontSize: 14, color: 'var(--af-muted)', lineHeight: 1.6, margin: 0 }}>
                  {initial
                    ? translate('pages.public.reset_password.reset_password.p.password_create_next')
                    : translate('pages.public.reset_password.reset_password.p.password_2')}
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <label className="af-label">
                  {translate('pages.public.reset_password.reset_password.label_label.password')}

                  <span style={{ position: 'relative' }}>
                    <input
                      className="af-input af-mono"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={'••••••••'}
                      required
                      minLength={8}
                      style={{ paddingRight: 44 }}
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={
                        showPassword
                          ? translate(
                              'pages.public.reset_password.reset_password.button.aria_label_password',
                            )
                          : translate(
                              'pages.public.reset_password.reset_password.button.aria_label_password_2',
                            )
                      }
                      style={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 0,
                        color: 'var(--af-muted)',
                        padding: 2,
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"
                          stroke="currentColor"
                          strokeWidth="1.4"
                        />

                        <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
                        {showPassword && (
                          <line
                            x1="2"
                            y1="2"
                            x2="14"
                            y2="14"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                          />
                        )}
                      </svg>
                    </button>
                  </span>
                </label>

                <label className="af-label">
                  {translate(
                    'pages.public.reset_password.reset_password.label_label.password_confirmation',
                  )}

                  <input
                    className="af-input af-mono"
                    type={showPassword ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={'••••••••'}
                    required
                    minLength={8}
                  />
                </label>

                {error && <div className="af-error">{error}</div>}

                <button
                  className="af-primary-btn"
                  type="submit"
                  disabled={loading}
                  style={{ marginTop: 2 }}
                >
                  {loading ? (
                    <>
                      <Spinner />{' '}
                      {translate(
                        'pages.public.reset_password.reset_password.button_primary_btn.label',
                      )}
                    </>
                  ) : initial ? (
                    translate(
                      'pages.public.reset_password.reset_password.button_primary_btn.password_create',
                    )
                  ) : (
                    translate(
                      'pages.public.reset_password.reset_password.button_primary_btn.password',
                    )
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

function getSafeRedirect(value: string | null) {
  if (!value?.startsWith('/') || value.startsWith('//')) return null;
  return value;
}
